import { useEffect, useState } from 'react';
import { PETS_QUESTION } from 'shared/constants/PETS_QUESTIONS';
import { IPetFormProps, PetCheckBoxProps } from 'shared/interfaces/IPets';
import { ITalentPets } from 'shared/interfaces/IPets';
import { petService } from 'shared/services/petService';

const { getAnimal, getBreed, getSize } = petService();

// Determines if the Selected Animal or Selected Breed is an Others
const isOthers = (value: string, data?: string[]) => data?.includes(value);

// Fixed Data if Breed is others
const otherSizeData = ['Extra Small', 'Small', 'Medium', 'Large', 'Extra Large'];

export const usePetForm = (data: ITalentPets | undefined) => {
  // The State thats always required and use in Autocomplete
  const [selectedAnimal, setSelectedAnimal] = useState<string>(data?.animal || '');
  const [selectedBreed, setSelectedBreed] = useState<string>(data?.breed || '');
  const [selectedSize, setSelectedSize] = useState<string>(data?.size || '');
  const [selectedAge, setSelectedAge] = useState<number | undefined>(data?.age !== undefined ? data?.age : undefined);

  // The state that is required but in Text Field
  const [selectedOtherAnimal, setSelectedOtherAnimal] = useState<string>(data?.animal || '');
  const [selectedOtherBreed, setSelectedOtherBreed] = useState<string>(data?.breed || '');

  // Optional States
  const [otherOptions, setOtherOptions] = useState<string[]>(data?.others || []);

  // Flag to show if Age is in months or years
  const [ageInMonths, setAgeInMonths] = useState<boolean | undefined>(data?.age_in_months || undefined);

  // Get Animals Data
  const { data: animalsData, isLoading: animalLoading } = getAnimal();

  // Check if the Animal is Others
  const isAnimalOther =
    selectedAnimal === 'Others' || (Boolean(selectedAnimal) && data && !isOthers(selectedAnimal, animalsData?.animals));

  // Get Breeds Data
  const { data: breedsData, isLoading: breedsLoading } = getBreed({
    animal: isAnimalOther ? '' : selectedAnimal,
  });

  // Check if the Breed is Others
  const isBreedOther =
    isAnimalOther ||
    selectedBreed === 'Others' ||
    (Boolean(selectedBreed) && data && !isOthers(selectedBreed, breedsData?.breeds));

  // Get Size Data
  const { data: sizesData, isLoading: sizesLoading } = getSize({
    animal: isAnimalOther ? '' : selectedAnimal,
    breed: isBreedOther ? '' : selectedBreed,
  });

  // Handle Other Options, This is being used if the Animal is Cat or Dog,
  const handleOptions = (value: string) => {
    if (otherOptions?.includes(value)) {
      setOtherOptions((prev) => prev.filter((option) => option !== value));
    } else {
      setOtherOptions((prev) => [...prev, value]);
    }
  };

  // Animal Change
  const handleAnimalChange = (value: string) => {
    setSelectedAnimal(value);
    setSelectedBreed(value === 'Others' ? 'Others' : '');
    setSelectedSize('');
    setSelectedAge(undefined);
    setAgeInMonths(false);

    setSelectedOtherAnimal('');
    setSelectedOtherBreed('');
  };

  // Breed Change
  const handleABreedChange = (value: string) => {
    setSelectedBreed(value);
    setSelectedSize('');
    setSelectedAge(undefined);
    setAgeInMonths(false);

    setSelectedOtherBreed('');
  };

  // Size Change
  const handleSizeChange = (value: string) => {
    setSelectedSize(value);
    setSelectedAge(undefined);
    setAgeInMonths(false);
  };

  // Age Change
  const handleSetAgeInput = (value: string | number) => {
    if (Number(value) >= (ageInMonths ? 25 : 100)) {
      return;
    }

    if (value === '') {
      return setSelectedAge(undefined);
    }

    let numValue: string | number = Number(value);
    numValue = parseFloat(numValue.toFixed(1));
    if (numValue % 1 === 0) {
      numValue = numValue.toFixed(0);
    }
    setSelectedAge(Number(numValue));
  };

  // A function to save the inputted Value, this is being used if the Animal or Breed is others
  const handleOtherInputChange = (newValue: string, type?: 'animal' | 'breed') => {
    if (type === 'animal') {
      setSelectedOtherAnimal(newValue);
    } else {
      setSelectedOtherBreed(newValue);
    }
  };

  // Pet Forms
  const petForm: IPetFormProps[] = [
    {
      name: 'Animal',
      list: animalsData?.animals.concat(['Others']) || [],
      value: isAnimalOther ? selectedOtherAnimal : selectedAnimal,
      placeholder: isAnimalOther ? `Please input an animal` : selectedAnimal,
      isLoading: animalLoading,
      readOnly: selectedAnimal !== 'Others',
      othersMode:
        selectedAnimal === 'Others' ||
        (Boolean(selectedAnimal) && data && !isOthers(selectedAnimal, animalsData?.animals)),
      handleOnChange: handleAnimalChange,
      handleInputChange(value) {
        handleOtherInputChange(value, 'animal');
      },
    },
    {
      name: 'Breed',
      list: breedsData?.breeds?.concat(['Others']) || [],
      value: isBreedOther ? selectedOtherBreed : selectedBreed,
      disable: !selectedAnimal || (!selectedOtherAnimal && selectedAnimal === 'Others'),
      placeholder: isBreedOther ? `Please input a breed` : selectedBreed,
      isLoading: breedsLoading,
      othersMode: isBreedOther,
      readOnly: selectedBreed !== 'Others',
      handleOnChange: handleABreedChange,
      handleInputChange(value) {
        handleOtherInputChange(value, 'breed');
      },
    },
    {
      name: 'Size',
      list: selectedOtherBreed ? otherSizeData : sizesData?.size || [],
      value: selectedSize,
      placeholder: selectedSize,
      disable: !selectedAnimal || (!selectedBreed && !isBreedOther) || (!selectedOtherBreed && isBreedOther),
      isLoading: sizesLoading,
      readOnly: true,
      handleOnChange: handleSizeChange,
    },
    {
      name: 'Age',
      list: [],
      value: selectedAge !== undefined ? selectedAge.toString() : '',
      handleOnChange: () => {
        setSelectedAge(undefined);
      },
      handleInputChange: handleSetAgeInput,
      placeholder: 'Type age',
      disable:
        !selectedAnimal || (!selectedBreed && !isBreedOther) || (!selectedOtherBreed && isBreedOther) || !selectedSize,
    },
  ];

  // CheckBox Options
  const checkBoxOptions: PetCheckBoxProps = {
    handleChange: handleOptions,
    list: PETS_QUESTION[selectedAnimal?.toLowerCase()],
    isChecked: (value: string) => otherOptions?.includes(value),
    showCheckBox: selectedAnimal?.toLowerCase() === 'dog' || selectedAnimal?.toLowerCase() === 'cat',
    handleShowMonths: () => {
      setAgeInMonths((prev) => !prev);
      setSelectedAge(undefined);
    },
    ageInMonths,
    handleMonthsChange: (value: string) => {
      setSelectedAge(value !== null ? Number(value) : undefined);
    },
  };

  useEffect(() => {
    if (data) {
      setSelectedAnimal(data.animal || '');
      setSelectedBreed(data?.breed || '');
      setSelectedSize(data?.size || '');
      setAgeInMonths(data?.age_in_months);
    }
  }, [data]);

  return {
    petForm,
    selectedAnimal: isAnimalOther ? selectedOtherAnimal : selectedAnimal,
    selectedBreed: isBreedOther ? selectedOtherBreed : selectedBreed,
    selectedSize,
    selectedAge,
    ageInMonths,
    otherOptions,
    checkBoxOptions,
  };
};
